import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "use-intl";

import { EditorialBreadcrumbs } from "@/features/editorial-hub/editorial-breadcrumbs";
import { getFeaturePageLandingQueryOptions } from "@/features/feature-page-landing/feature-page-landing-query";
import { FeaturePageCard } from "@/features/feature-page/feature-page-card";
import type { FeaturePageFamily } from "@garimpa/lib/featurePage";
import type { ViewerLocale } from "@garimpa/lib/viewerPreferences";

export function FeaturePageLandingContent({
  family,
  locale,
}: {
  family: FeaturePageFamily;
  locale: ViewerLocale;
}) {
  const t = useTranslations("featurePageLanding");
  const landingQuery = useQuery(getFeaturePageLandingQueryOptions({ family, locale }));
  const items = landingQuery.data?.items ?? [];

  return (
    <section
      aria-labelledby="feature-page-landing-title"
      className="relative overflow-hidden px-4 py-12 sm:px-6 sm:py-16 lg:px-8 lg:py-20"
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-96 bg-[radial-gradient(circle_at_top_left,hsl(var(--primary)/0.14),transparent_58%)]" />
      <div className="mx-auto max-w-[1440px]">
        <header className="max-w-4xl">
          <EditorialBreadcrumbs family={family} locale={locale} />
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-primary">
            {t("eyebrow")}
          </p>
          <h1
            id="feature-page-landing-title"
            className="mt-3 text-5xl font-black tracking-tight sm:text-6xl lg:text-7xl"
          >
            {t(`${family}.title`)}
          </h1>
          <p className="mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground sm:text-lg">
            {t(`${family}.description`)}
          </p>
        </header>

        {items.length ? (
          <ul className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
            {items.map((item, index) => (
              <li key={item.id} className={index === 0 ? "md:col-span-2" : undefined}>
                <FeaturePageCard
                  item={item}
                  locale={locale}
                  featured={index === 0}
                  className="block h-full"
                />
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-10 rounded-3xl border border-dashed border-border bg-card/40 px-6 py-20 text-center text-sm text-muted-foreground">
            {t("empty")}
          </div>
        )}
      </div>
    </section>
  );
}
